import React from 'react';
import { Link } from 'react-router-dom';
import { GameWallet } from '../../../types';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../ui/shadcn';
import Button from '../../ui/Button';
import { useSyncGameWallet } from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { cn, formatCurrency } from '../../../utils';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface GameWalletDrawerProps {
  wallet: GameWallet | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

const syncStatusLabel: Record<string, { label: string; className: string }> = {
  never_synced: { label: 'Never Synced', className: 'bg-gray-100 text-gray-800' },
  in_sync: { label: 'In Sync', className: 'bg-green-100 text-green-800' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
};

const GameWalletDrawer: React.FC<GameWalletDrawerProps> = ({
  wallet,
  isOpen,
  onClose,
  onRefresh,
}) => {
  const fmt = useFormatDate();
  // Retry Sync is a write (provider ops) — hidden for view-only roles (platform_admin).
  const canSync = usePermission('gameProviderManagement');
  const { mutateAsync: syncWallet, isLoading: isSyncing } = useSyncGameWallet();

  const handleRetrySync = async () => {
    if (!wallet) return;
    try {
      await syncWallet(wallet.id);
      toast.success('Sync job queued successfully');
      onRefresh?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to retry sync');
    }
  };

  const latestError = wallet?.errorLogs?.[0];
  const statusConfig = wallet ? (syncStatusLabel[wallet.syncStatus] ?? { label: wallet.syncStatus, className: 'bg-gray-100 text-gray-800' }) : null;

  const diff = wallet
    ? parseFloat(wallet.walletBalance) - parseFloat(wallet.inGameBalance)
    : 0;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-[420px] max-w-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Wallet Detail</SheetTitle>
          <SheetDescription>
            Quick investigation view for one player game wallet.
          </SheetDescription>
        </SheetHeader>

        {wallet && (
          <div className="mt-6 space-y-1">
            <Row label="Player">
              <Link
                to={`/users/${encodeURIComponent(wallet.userId)}`}
                className="font-medium text-primary hover:underline"
              >
                {wallet.user?.email || wallet.user?.phone || wallet.userId}
              </Link>
            </Row>
            <Row label="Provider">
              <span>{wallet.provider?.name ?? wallet.provider?.code ?? wallet.gameProviderId}</span>
            </Row>
            <Row label="Game Wallet ID">
              <span className="font-mono text-xs">{wallet.id}</span>
            </Row>
            {wallet.gameUsername && (
              <Row label="Game Username">
                <span>{wallet.gameUsername}</span>
              </Row>
            )}
            <Row label="Master Credit Balance">
              <span className="font-semibold">{formatCurrency(parseFloat(wallet.walletBalance))}</span>
            </Row>
            <Row label="Provider Balance">
              <span className="font-semibold">{formatCurrency(parseFloat(wallet.inGameBalance))}</span>
            </Row>
            <Row label="Difference">
              <span className={cn(
                'font-semibold',
                Math.abs(diff) > 0 ? 'text-destructive' : 'text-green-700',
              )}>
                {diff > 0 ? '+' : ''}{formatCurrency(diff)}
              </span>
            </Row>
            <Row label="Sync Status">
              {statusConfig && (
                <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', statusConfig.className)}>
                  {statusConfig.label}
                </span>
              )}
            </Row>
            <Row label="Last Sync">
              <span>{wallet.lastSyncedAt ? fmt(wallet.lastSyncedAt) : '—'}</span>
            </Row>
            {latestError && (
              <Row label="Last Error">
                <span className="text-destructive text-xs truncate max-w-[200px]" title={latestError.errorMessage ?? ''}>
                  {latestError.errorMessage ?? '—'}
                </span>
              </Row>
            )}

            <div className="pt-4 flex gap-2 flex-wrap">
              {canSync && (
                <Button
                  size="sm"
                  onClick={() => void handleRetrySync()}
                  disabled={isSyncing}
                >
                  <RefreshCw className={cn('h-4 w-4 mr-2', isSyncing && 'animate-spin')} />
                  {isSyncing ? 'Retrying…' : 'Retry Sync'}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>

            {canSync && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                <span>Retry sync will not create a new deposit or withdrawal transaction. It only retries the wallet sync job.</span>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-start border-b border-border py-2.5 text-sm">
    <span className="text-muted-foreground shrink-0 mr-4">{label}</span>
    <span className="text-right">{children}</span>
  </div>
);

export default GameWalletDrawer;
